import React, { useState } from 'react';
import Button from '../Button';
import ConfirmDialog from '../ConfirmDialog';
import { Menu, MenuItem } from '../Menu';
import { useMoneyFormat, useMoneyInParenthesesFormat, useDateFormat } from '../../context/LocaleContext';
import {
  familyDisplayName, studentStatus, contactCardName,
  DELETE_FAMILY_TITLE, DELETE_FAMILY_MESSAGE, familyDeletionRefusal,
} from '../../domain/people';
import useNames from '../../hooks/useNames';
import s from './families.module.css';
import t from '../SimpleTable.module.css';
import Unread from '../Unread';
import { READING_WORDING as RW, figureOrDash } from '../../domain/reading';
import { canVoidEntry, ledgerPillMinor, ledgerRowView, voidConfirmMessage } from '../../domain/familyAccount';

// Who this account covers and who gets billed.
//
// Siblings share one balance, so the student list answers "what am I invoicing
// for" and the Invoice Recipient badge answers "who receives it" — both easy to
// get wrong from the balance alone.
//
// Family Contacts draws every contact (#358), as the student's Family
// Contacts tab does and as MMS's family card does: SPEC.md §3.2 has it as
// "Students (w/ status), Family Contacts (w/ `Invoice Recipient` badge)", and
// MMS lists a contact whose `Set as Preferred Invoice Recipient` is unticked,
// in place, with the badge absent.
//
// Two badges at once is a state to draw: `Set as Preferred Invoice Recipient`
// is not exclusive in MMS, so this card renders more than one badge and picks
// no winner between them.
//
// The server does pick, one layer down: it addresses an invoice to one person,
// and `familyBillingContact` in src/domain/people.js takes the first flagged
// contact in the list's order (#319). So the one actually billed carries
// `Billed` beside its badge. That word is ours: MMS never has to choose.
//
// The family document carries no contact (#319); there is one list in one
// order.
function AccountCard({ family, students, contacts, billingContact, reads, failures }) {
  const names = useNames();
  return (
    <div className={s.card}>
      <div>
        <div className={s.cardLabel}>Students</div>
        {students.length === 0 && !reads.students ? (
          <Unread failed={failures.students} />
        ) : students.length === 0 ? (
          <div className={s.none}>{RW['family.noStudents']}</div>
        ) : students.map((st) => (
          <div key={st.id} className={s.person}>
            <span className={s.personName}>{names.student(st)}</span>
            <span className={s.badgeActive}>{studentStatus(st.status)}</span>
          </div>
        ))}
      </div>

      <div>
        <div className={s.cardLabel}>Family Contacts</div>
        {contacts.length === 0
          ? (reads.contacts ? <div className={s.none}>None</div> : <Unread failed={failures.contacts} />)
          : null}
        {/* `contactCardName` rather than `names.familyContact`, the split the
            student's tab makes: the Parent Name Format binds the person a
            message is addressed to, and a contact record here is a heading on
            the teacher's own screen. A record carries a Title, and the two
            functions treat it differently on purpose. */}
        {contacts.map((c) => (
          <React.Fragment key={c.id}>
            <div className={s.person}>
              <span className={s.personName}>{contactCardName(c)}</span>
              {c.isInvoiceRecipient ? (
                <span className={s.badgeRecipient}>Invoice Recipient</span>
              ) : null}
              {c.id && c.id === billingContact?.id ? (
                <span className={s.badgeBilled}>Billed</span>
              ) : null}
            </div>
            {c.email ? <div className={s.email}>{c.email}</div> : null}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

// Charges and payments are separate columns rather than one signed number: an
// invoice is read across, and a minus sign in a money column is easy to miss.
// Which column, the Description's fallback and the `Lesson` chip are
// `ledgerRowView` (src/domain/familyAccount.js).
//
// The amount is MMS's pill, the kind's word before the figure — `Charge
// $30.00` — because a column headed Charges & Discounts holds both and a bare
// `$10.00` cannot say which. A discount sits in that column (#643, MMS's
// heading), in the colour of something that lowers the balance. The word and
// the colour are `ledgerRowView`'s `label` and `tone`.
//
// A discount and a refund take from their column, and MMS writes their figure
// in parentheses — `Discount ($10.00)`, `Refund ($5.00)` beside `Payment
// $50.00` (#649) — as its Delete Transaction sentence does (`the
// ($50.00)discount`). `ledgerPillMinor` is the figure signed in its column;
// `formatMoneyInParentheses` writes it.
const PILL = { owe: s.owe, credit: s.credit };

// `Delete Family` (#346).
//
// No MMS reading for this control — its placement, its copy, or whether MMS
// offers one (#346) — so it is ours; `planFamilyDeletion` in
// src/domain/people.js argues the behaviour.
//
// Beside `+ Add Transaction` rather than in a `⋮`: this page has no header
// kebab, and the row kebabs belong to the ledger rows. Secondary, so it does not
// read as the thing to do on the screen.
//
// The refusal arrives after the confirm: whether a family can go is eight
// server queries (`FAMILY_REFERENCES`), so there is no free way to disable the
// button up front, and a disabled control with no sentence is the refusal shape
// this app avoids. The probe runs on Delete, and a family that cannot go comes
// back with the reason in the same dialog.
export default function FamilyDetail({
  family, students, ledger, onBack, onAddTransaction, onVoidEntry,
  // The family's contacts (#358). Defaulted rather than required, as
  // `onDeleteFamily` below is: a caller that has not wired the collection gets
  // an account card saying the family has none rather than one that throws.
  contacts = [],
  // Which contact the studio's post is addressed to, from
  // `useFamilies().billingContactFor` (#319). Null is a legitimate value — a
  // family with no contacts at all — and draws no `Billed` marker.
  billingContact = null,
  // Optional: a caller that does not wire it gets the page without the
  // control rather than a button that does nothing.
  onDeleteFamily = null,
  // Which of the lists above have been read (#629): an unread one says
  // nothing — no "None", no "No transactions yet.", no balance of zero.
  // Defaulted to read for this file's own tests, which hand it lists.
  reads = { students: true, contacts: true, ledger: true },
  // Which of them stopped before answering (#647).
  failures = {},
}) {
  const formatMoney = useMoneyFormat();
  const formatPill = useMoneyInParenthesesFormat();
  const shortDate = useDateFormat();
  const studentName = (id) => students.find((st) => st.id === id)?.firstName || '–';

  // Which row's kebab is open, and which entry is pending the delete confirm.
  // Two pieces of state rather than one: the menu closes the instant an item
  // is clicked (see onClick below), but the dialog it opened needs to survive
  // that close.
  const [rowMenuId, setRowMenuId] = useState(null);
  const [confirmEntry, setConfirmEntry] = useState(null);

  // The family delete's three states: the confirm is open, a probe is in
  // flight, and the refusal a completed probe came back with. `refusal` is a
  // sentence or null — it is rendered inside the same dialog, so the teacher
  // reads the answer where they asked the question.
  const [confirmFamily, setConfirmFamily] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [refusal, setRefusal] = useState(null);

  const closeFamilyConfirm = () => { setConfirmFamily(false); setRefusal(null); };
  const confirmDeleteFamily = async () => {
    setDeleting(true);
    setRefusal(null);
    let plan = null;
    let threw = null;
    try {
      // The `await` is the only statement in the `try`, so this catch is for
      // the delete's own failure, not a bug in the lines below it (#184).
      plan = await onDeleteFamily?.(family.id);
    } catch (err) {
      threw = err;
    } finally {
      // In `finally` because the button must come back whichever way it went.
      // A rejected write leaves the dialog open with the control live rather
      // than stuck on `Checking…`.
      setDeleting(false);
    }
    if (threw) {
      console.error('[families] the family delete did not go through:', threw);
      // A rejection here is a refused write, a lost connection or a bug in the
      // handler, and nothing at this point tells them apart, so the sentence
      // offers that rather than picking one.
      setRefusal(
        'This family was not deleted. The write did not go through — the console has '
        + 'the error, which may be a refused permission, a lost connection, or a fault '
        + 'here; this cannot tell them apart.',
      );
      return;
    }
    // A caller that returned nothing is not a refusal — it is a caller that
    // does not report, and the page will have navigated away if it deleted.
    const message = plan ? familyDeletionRefusal(plan) : null;
    if (message) setRefusal(message);
    else setConfirmFamily(false);
  };

  const closeMenu = () => setRowMenuId(null);
  const requestVoid = (e) => { closeMenu(); setConfirmEntry(e); };
  const confirmVoid = async () => {
    const entry = confirmEntry;
    setConfirmEntry(null);
    if (entry) await onVoidEntry?.(entry.id);
  };

  return (
    <>
      <button className={s.link} onClick={onBack}>‹ Back to Families &amp; Invoices</button>

      <div>
        <div className={s.head}>{familyDisplayName(family)}</div>
        <div className={s.sub}>Balance owing: {figureOrDash(reads.ledger, formatMoney(family.balanceMinor))}</div>
      </div>

      <AccountCard
        family={family}
        students={students}
        contacts={contacts}
        billingContact={billingContact}
        reads={reads}
        failures={failures}
      />

      <div className={s.bar}>
        <Button onClick={onAddTransaction}>+ Add Transaction</Button>
        {onDeleteFamily ? (
          <Button variant="secondary" onClick={() => setConfirmFamily(true)}>
            Delete Family
          </Button>
        ) : null}
      </div>

      <div className={t.wrap}>
        <table className={t.table}>
          <thead>
            <tr>
              <th>Date</th><th>Student</th><th>Description</th>
              <th>Charges &amp; Discounts</th><th>Payments &amp; Refunds</th><th>Balance</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {ledger.map((e) => {
              const row = ledgerRowView(e);
              return (
              <tr key={e.id}>
                <td>
                  {/* The studio's date pattern rather than the raw ISO string.
                      Substituted into, never parsed — a ledger date drawn
                      through `new Date()` reads a day early west of Greenwich. */}
                  {shortDate(e.occurredOn) || e.occurredOn}
                  {row.lessonBadge ? <span className={s.badgeLesson}>Lesson</span> : null}
                </td>
                <td>{e.studentId ? studentName(e.studentId) : '–'}</td>
                <td>{row.description}</td>
                {['charges', 'payments'].map((column) => (
                  <td key={column}>
                    {row.column === column ? (
                      <span className={PILL[row.tone] || s.owe}>{row.label} {formatPill(row.sign * e.amountMinor)}</span>
                    ) : '–'}
                  </td>
                ))}
                <td>{formatMoney(e.runningBalanceMinor)}</td>
                <td className={s.rowMenuCell}>
                  {/* Which rows offer this, and why some do not, is
                      `canVoidEntry`, shared with the cross-family Transactions
                      tab (#191). */}
                  {canVoidEntry(e) ? (
                    <Menu
                      open={rowMenuId === e.id}
                      onClose={closeMenu}
                      trigger={(
                        <button
                          className={s.kebab}
                          aria-label="Row actions"
                          onClick={() => setRowMenuId((cur) => (cur === e.id ? null : e.id))}
                        >
                          ⋮
                        </button>
                      )}
                    >
                      <MenuItem icon="trash" onClick={() => requestVoid(e)}>
                        Delete Transaction
                      </MenuItem>
                    </Menu>
                  ) : null}
                </td>
              </tr>
              );
            })}
            {ledger.length === 0 ? (
              <tr><td colSpan={7} className={t.muted}>
                {reads.ledger ? RW['family.noTransactions'] : <Unread as="span" failed={failures.ledger} />}
              </td></tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {/* Copy mirrors MMS's own delete-transaction confirm: names the amount,
          the kind, the family and the date, and asks for nothing else. No
          reason field — see `canVoidEntry` for why. */}
      <ConfirmDialog
        open={Boolean(confirmEntry)}
        title="Delete Transaction"
        message={voidConfirmMessage(confirmEntry, {
          familyName: familyDisplayName(family),
          amountText: confirmEntry ? formatPill(ledgerPillMinor(confirmEntry)) : '',
          dateText: confirmEntry ? shortDate(confirmEntry.occurredOn) : '',
        })}
        confirmLabel="Delete"
        discardLabel="Cancel"
        cancelLabel=""
        onDiscard={() => setConfirmEntry(null)}
        onCancel={() => setConfirmEntry(null)}
        onConfirm={confirmVoid}
      />

      {/* One dialog for both halves of the family delete. Before the probe it
          carries the generic warning; after a refusal it carries the reason and
          the confirm button is gone, so the only way out is Cancel. */}
      <ConfirmDialog
        open={confirmFamily}
        title={DELETE_FAMILY_TITLE}
        message={refusal || DELETE_FAMILY_MESSAGE}
        confirmLabel={deleting ? 'Checking…' : 'Delete'}
        confirmDisabled={deleting}
        discardLabel="Cancel"
        cancelLabel=""
        onDiscard={closeFamilyConfirm}
        onCancel={closeFamilyConfirm}
        // `null` removes the confirm button entirely (see ConfirmDialog), which
        // is what a refused delete wants: there is nothing left to confirm and
        // a greyed-out Delete would invite a second try at the same answer.
        onConfirm={refusal ? null : confirmDeleteFamily}
      />
    </>
  );
}
